<template>
  <section
    class="filter-bar border rounded bg-body-emphasis p-3"
    aria-label="Chat log filters"
  >
    <form class="search" role="search" @submit.prevent="commitSearch">
      <label :for="searchId" class="visually-hidden">Search messages</label>
      <div class="input-group">
        <span class="input-group-text"
          ><FontAwesomeIcon icon="magnifying-glass"
        /></span>
        <input
          :id="searchId"
          ref="searchInput"
          v-model="searchDraft"
          type="search"
          class="form-control"
          placeholder='Search messages, e.g. hydro 2027 -"off"'
          autocomplete="off"
          :aria-describedby="hintId"
          @input="debouncedCommitSearch"
          @keydown.esc="clearSearch"
        />
        <kbd class="input-group-text shortcut" aria-hidden="true">/</kbd>
      </div>
      <p :id="hintId" class="form-text mb-0">
        Every word must match · <code>"exact phrase"</code> ·
        <code>-exclude</code> · searches message, sender and conversation
      </p>
    </form>
    <div class="d-flex flex-wrap align-items-end gap-2">
      <label class="small text-muted">
        Sender
        <input
          v-model="senderDraft"
          class="form-control form-control-sm"
          type="search"
          placeholder="Any sender"
          @input="debouncedCommitSender"
        />
      </label>
      <label class="small text-muted">
        From (UTC)
        <input
          class="form-control form-control-sm"
          type="date"
          :value="model.since ?? ''"
          :max="model.until ?? undefined"
          @change="update({ since: inputValue($event) || null })"
        />
      </label>
      <label class="small text-muted">
        To (UTC)
        <input
          class="form-control form-control-sm"
          type="date"
          :value="model.until ?? ''"
          :min="model.since ?? undefined"
          @change="update({ until: inputValue($event) || null })"
        />
      </label>
      <div
        class="btn-group btn-group-sm"
        role="group"
        aria-label="Trade signals"
      >
        <button
          v-for="option in signalOptions"
          :key="option.label"
          type="button"
          class="btn"
          :class="
            model.signals === option.value
              ? 'btn-primary'
              : 'btn-outline-primary'
          "
          :aria-pressed="model.signals === option.value"
          :title="option.title"
          @click="update({ signals: option.value })"
        >
          {{ option.label }}
        </button>
      </div>
      <select
        class="form-select form-select-sm w-auto"
        aria-label="Sort order"
        :value="model.order"
        @change="
          update({
            order: inputValue($event) === 'oldest' ? 'oldest' : 'newest',
          })
        "
      >
        <option value="newest">Newest first</option>
        <option value="oldest">Oldest first</option>
      </select>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="btn btn-link btn-sm"
        @click="clearAll"
      >
        Clear filters
      </button>
    </div>
  </section>
</template>
<script setup lang="ts">
import { onKeyStroke, useDebounceFn } from "@vueuse/core";
import { computed, ref, useId, useTemplateRef, watch } from "vue";
import type { ChatLogFilters } from "./tradeChatLogsTypes";

const model = defineModel<ChatLogFilters>({ required: true });

const SEARCH_DEBOUNCE_MS = 350;
const signalOptions: {
  label: string;
  value: ChatLogFilters["signals"];
  title: string;
}[] = [
  { label: "All", value: null, title: "Every message" },
  {
    label: "Trade signals",
    value: "with",
    title: "Messages the classifier turned into bids, asks or trades",
  },
  {
    label: "Chatter",
    value: "without",
    title: "Messages without any extracted trade signal",
  },
];
const searchId = useId();
const hintId = useId();
const searchInput = useTemplateRef<HTMLInputElement>("searchInput");
const searchDraft = ref(model.value.q);
const senderDraft = ref(model.value.sender);

const hasActiveFilters = computed(
  () =>
    Boolean(
      model.value.q ||
      model.value.sender ||
      model.value.since ||
      model.value.until ||
      model.value.signals
    ) || model.value.order !== "newest"
);

// Back/forward navigation changes the URL under the inputs; keep the drafts in step.
watch(
  () => model.value.q,
  (q) => {
    if (q !== searchDraft.value.trim()) searchDraft.value = q;
  }
);
watch(
  () => model.value.sender,
  (sender) => {
    if (sender !== senderDraft.value.trim()) senderDraft.value = sender;
  }
);

onKeyStroke("/", (event) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest("input, textarea, select, [contenteditable]")) return;
  event.preventDefault();
  searchInput.value?.focus();
});

const update = (changes: Partial<ChatLogFilters>) => {
  model.value = { ...model.value, ...changes };
};
const inputValue = (event: Event) =>
  (event.target as HTMLInputElement | HTMLSelectElement).value;

function commitSearch() {
  if (searchDraft.value.trim() !== model.value.q)
    update({ q: searchDraft.value.trim() });
}
const debouncedCommitSearch = useDebounceFn(commitSearch, SEARCH_DEBOUNCE_MS);
const debouncedCommitSender = useDebounceFn(() => {
  if (senderDraft.value.trim() !== model.value.sender)
    update({ sender: senderDraft.value.trim() });
}, SEARCH_DEBOUNCE_MS);

function clearSearch() {
  searchDraft.value = "";
  commitSearch();
}
function clearAll() {
  searchDraft.value = "";
  senderDraft.value = "";
  update({
    q: "",
    sender: "",
    since: null,
    until: null,
    signals: null,
    order: "newest",
  });
}
</script>
<style scoped lang="scss">
.filter-bar {
  display: grid;
  gap: 0.75rem;
}
.shortcut {
  font-size: 0.75rem;
  color: var(--phoenix-secondary-color);
}
</style>
